import { type RedeemInvitationResponse, redeemInvitationResponseSchema } from '@diario/common';
import { queryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { apiGet } from '../../../lib/http';

/**
 * Opciones de `GET /invitations/redeem/:token` (documento 04, §6), parametrizadas por
 * `token` y exportadas aparte del hook porque la ruta `/crear-experiencia` las necesita
 * para resolver la puerta correcta (`resolverPuertaCreacion`) antes de pintar nada, con
 * `ensureQueryData` en su `beforeLoad` — mismo motivo que `sesionQueryOptions`.
 *
 * `enabled` es la mitad del contrato de este hook: el asistente se abre igual sin enlace
 * (`?inv=` ausente), así que sin `token` no hay nada que comprobar y la query nunca se
 * dispara. El enlace se comprueba, nunca se consume aquí — el consumo es transaccional al
 * crear la experiencia (decisiones.md, decisión 1).
 */
export const comprobarEnlaceQueryOptions = (token?: string) =>
	queryOptions({
		enabled: token !== undefined && token.length > 0,
		queryFn: () => apiGet(`/invitations/redeem/${encodeURIComponent(token as string)}`, redeemInvitationResponseSchema),
		queryKey: ['invitations', 'redeem', token] as const,
	});

/**
 * Comprueba, sin consumir, si un enlace de invitación es válido para quien pregunta
 * (uno de los cinco resultados de `redeemInvitationResultSchema`: `ok`, `wrong-user`,
 * `expired`, `used`, `not-found`). Sin `token` no pide nada — ver `comprobarEnlaceQueryOptions`.
 */
export const useComprobarEnlace = (token?: string): UseQueryResult<RedeemInvitationResponse> =>
	useQuery(comprobarEnlaceQueryOptions(token));
